import { Check, Copy, KeyRound, RotateCw, Trash2 } from "lucide-react";
import { useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";
import type { WorkflowWebhookTrigger } from "@/types/workflow.types";

interface WebhookTriggerRowProps {
  trigger: WorkflowWebhookTrigger;
  publicPath: string;
  confirmDelete: boolean;
  busy: boolean;
  canWrite: boolean;
  onCopy: (value: string, message: string) => void;
  onToggle: () => void;
  onRotate: () => void;
  onDelete: () => void;
  onCancelDelete: () => void;
}

export function WebhookTriggerRow({
  trigger,
  publicPath,
  confirmDelete,
  busy,
  canWrite,
  onCopy,
  onToggle,
  onRotate,
  onDelete,
  onCancelDelete,
}: WebhookTriggerRowProps) {
  const t = useTranslations("workflows");
  const path = `${publicPath}${trigger.publicId}`;
  const statusClass = trigger.enabled
    ? "bg-emerald-100 text-emerald-700"
    : "bg-gray-100 text-gray-600";

  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-[var(--foreground)]">{trigger.name}</h4>
            <span className={`rounded-full px-2 py-0.5 text-xs ${statusClass}`}>
              {trigger.enabled ? t("webhooks.enabled") : t("webhooks.disabled")}
            </span>
          </div>
          <button
            className="break-all text-left font-mono text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
            onClick={() => onCopy(path, t("webhooks.toast_path_copied"))}
          >
            {path}
          </button>
          <p className="flex items-center gap-1.5 text-xs text-[var(--muted-foreground)]">
            <KeyRound className="size-3.5" />
            {t("webhooks.secret_prefix", { prefix: trigger.secretPrefix })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            startIcon={<Copy className="size-4" />}
            onClick={() => onCopy(path, t("webhooks.toast_path_copied"))}
          >
            {t("webhooks.copy_path")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            startIcon={trigger.enabled ? <Check className="size-4" /> : undefined}
            disabled={!canWrite || busy}
            onClick={onToggle}
          >
            {trigger.enabled ? t("webhooks.disable") : t("webhooks.enable")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            startIcon={<RotateCw className="size-4" />}
            disabled={!canWrite || busy}
            onClick={onRotate}
          >
            {t("webhooks.rotate")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            startIcon={<Trash2 className="size-4" />}
            disabled={!canWrite || busy}
            onClick={onDelete}
          >
            {confirmDelete ? t("webhooks.confirm_delete") : t("webhooks.delete")}
          </Button>
          {confirmDelete && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={onCancelDelete}>
              {t("webhooks.cancel_delete")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
